import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import {
  Trophy, Zap, Target, History, Wrench, Wallet, Activity, MessageCircle, Star,
  BookOpen, Newspaper, Flame, ChevronRight, ShieldCheck, DollarSign, Headphones, Hammer, Check,
} from "lucide-react";
import { api } from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import {
  QUICK_ACCESS, PERFORMANCE, FEATURES_INCLUDED,
  TESTIMONIAL, VALUE_STRIP, EDUCATION_TOPICS,
} from "@/lib/content";
import LiveTicker from "@/components/LiveTicker";
import TopPicks from "@/components/TopPicks";
import RecentResults from "@/components/RecentResults";
import UpcomingGames from "@/components/UpcomingGames";
import SportIconRow from "@/components/SportIconRow";
import SectionTitle from "@/components/SectionTitle";
import EventCard from "@/components/EventCard";
import FightCard from "@/components/FightCard";
import NewsCard from "@/components/NewsCard";
import MembershipShields from "@/components/MembershipShields";
import ResponsibleBanner from "@/components/ResponsibleBanner";

const ICONS = { target: Target, history: History, wrench: Wrench, wallet: Wallet, activity: Activity, "message-circle": MessageCircle, shield: ShieldCheck, hammer: Hammer, "dollar-sign": DollarSign, headphones: Headphones };
const CHART_DATA = [8, 12, 10, 16, 14, 20, 19, 26, 24, 32, 30, 40].map((v, i) => ({ i, v }));

function LastUpdated({ meta }) {
  if (!meta?.last_updated) return null;
  const d = new Date(meta.last_updated);
  return (
    <span className="text-[11px] font-mono-num text-slate-500" data-testid="last-updated">
      LAST UPDATED: {d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
    </span>
  );
}

export default function Home() {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {});
    api.get("/news", { params: { limit: 6 } }).then((r) => setNews(r.data || [])).catch(() => {});
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const live = events.filter((e) => e.status === "live");
  const upcoming = events.filter((e) => e.status === "upcoming");
  const finals = events.filter((e) => e.status === "final" && e.type === "team");
  const today = [...live, ...upcoming].filter((e) => e.type === "team").slice(0, 6);
  const fights = upcoming.filter((e) => e.type === "fight");

  return (
    <div>
      <LiveTicker />

      {/* HERO DASHBOARD */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 pt-6 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_320px] gap-5">
          {/* LEFT SIDEBAR */}
          <aside className="space-y-5 order-2 lg:order-1">
            <TopPicks />

            <div className="panel p-4" data-testid="member-quick-access">
              <div className="flex items-center gap-2 font-heading font-bold tracking-wide text-smasher-gold mb-3"><Star className="w-4 h-4" /> MEMBER QUICK ACCESS</div>
              <div className="grid grid-cols-3 gap-2">
                {QUICK_ACCESS.map((q) => {
                  const Ico = ICONS[q.icon] || Target;
                  return (
                    <Link key={q.label} to={q.to} className="flex flex-col items-center gap-1.5 p-2 rounded-lg bg-black/40 hover:bg-smasher-gold/10 transition text-center" data-testid={`quick-${q.icon}`}>
                      <Ico className="w-5 h-5 text-smasher-gold" />
                      <span className="text-[9px] font-bold text-slate-300 leading-tight">{q.label.toUpperCase()}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* CENTER HERO */}
          <div className="relative order-1 lg:order-2 hero-glow">
            <div className="relative rounded-2xl overflow-hidden panel">
              <img src={ASSETS.heroCenter} alt="Sportsbook Smasher" className="w-full h-auto object-cover" />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <Link to="/ebook" className="btn-gold flex-1 py-3.5 rounded-xl font-heading font-black tracking-wide text-center flex items-center justify-center gap-2" data-testid="hero-get-ebook-button">
                <BookOpen className="w-5 h-5" /> GET THE EBOOK
              </Link>
              <Link to="/football" className="btn-ghost-gold flex-1 py-3.5 rounded-xl font-heading font-black tracking-wide text-center" data-testid="hero-explore-sports-button">
                EXPLORE SPORTS
              </Link>
              <Link to="/membership" className="flex-1 py-3.5 rounded-xl font-heading font-black tracking-wide text-center bg-gradient-to-b from-orange-500 to-orange-600 text-white hover:brightness-110 transition" data-testid="hero-join-button">
                JOIN SPORTSBOOK SMASHER
              </Link>
            </div>
            <div className="mt-6">
              <SportIconRow />
            </div>
          </div>

          {/* RIGHT SIDEBAR */}
          <aside className="space-y-5 order-3">
            <div className="panel p-4" data-testid="performance-tracker">
              <div className="flex items-center gap-2 font-heading font-bold tracking-wide text-smasher-gold mb-3"><Trophy className="w-4 h-4" /> PERFORMANCE TRACKER</div>
              <div className="text-xs text-slate-400 font-bold">WIN STREAK</div>
              <div className="flex items-end justify-between">
                <div className="font-heading font-black text-5xl text-white leading-none">{PERFORMANCE.winStreak}<span className="text-sm text-slate-400 ml-1">DAYS</span></div>
                <div className="w-28 h-14">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={CHART_DATA}>
                      <defs>
                        <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="v" stroke="#10B981" strokeWidth={2} fill="url(#g)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 border-t border-white/5 pt-3">
                {[["THIS WEEK", PERFORMANCE.thisWeek], ["THIS MONTH", PERFORMANCE.thisMonth], ["ALL TIME", PERFORMANCE.allTime]].map(([k, v]) => (
                  <div key={k} className="text-center">
                    <div className="text-[8px] text-slate-500 tracking-wide">{k}</div>
                    <div className="font-mono-num font-bold text-emerald-400 text-sm">{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="panel p-4 flex items-center justify-between" data-testid="last-7-days">
              <div>
                <div className="flex items-center gap-2 font-heading font-bold tracking-wide text-smasher-gold mb-1 text-sm"><Zap className="w-4 h-4" /> LAST 7 DAYS</div>
                <div className="font-heading font-black text-3xl text-white">{PERFORMANCE.last7Record}</div>
                <div className="text-emerald-400 font-bold text-sm">{PERFORMANCE.last7Rate}% WIN RATE</div>
              </div>
              <div className="relative w-20 h-20">
                <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#1f2430" strokeWidth="3.5" />
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#10B981" strokeWidth="3.5" strokeDasharray={`${PERFORMANCE.last7Rate} 100`} strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center font-mono-num font-bold text-white">{PERFORMANCE.last7Rate}%</span>
              </div>
            </div>

            <RecentResults />
          </aside>
        </div>
        <div className="mt-3 text-center text-[10px] text-slate-600">Sample performance shown for illustration. Past results do not guarantee future outcomes.</div>
      </section>

      {/* MEMBERSHIP */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 py-10">
        <div className="text-center mb-8">
          <h2 className="font-heading font-black uppercase text-3xl sm:text-4xl lg:text-5xl text-gradient-gold">CHOOSE YOUR WEAPON. SMASH THE BOOKS.</h2>
        </div>
        <div className="grid lg:grid-cols-[260px_1fr_260px] gap-6 items-start">
          <div className="panel p-5">
            <div className="flex items-center gap-2 font-heading font-bold tracking-wide text-smasher-gold mb-4 text-sm"><Check className="w-4 h-4" /> FEATURES INCLUDED</div>
            <ul className="space-y-3">
              {FEATURES_INCLUDED.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-200"><Check className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" /> {f}</li>
              ))}
            </ul>
          </div>
          <MembershipShields />
          <div className="panel p-5 flex flex-col justify-center">
            <div className="flex gap-1 mb-3">{Array.from({ length: TESTIMONIAL.stars }).map((_, i) => <Star key={i} className="w-4 h-4 fill-smasher-gold text-smasher-gold" />)}</div>
            <p className="text-sm text-slate-300 italic leading-relaxed">"{TESTIMONIAL.quote}"</p>
            <p className="text-xs text-smasher-gold font-bold mt-3" data-testid="testimonial-author">— {TESTIMONIAL.author}</p>
            <p className="text-[10px] text-slate-500 font-bold tracking-wider mt-0.5">{TESTIMONIAL.role.toUpperCase()}</p>
          </div>
        </div>
      </section>

      {/* VALUE STRIP */}
      <section className="border-y border-smasher-gold/15 bg-black/60">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8 grid grid-cols-2 md:grid-cols-5 gap-6">
          {VALUE_STRIP.map((v) => {
            const Ico = ICONS[v.icon] || Target;
            return (
              <div key={v.title} className={`flex flex-col items-center text-center gap-2 ${v.center ? "md:scale-105" : ""}`}>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${v.center ? "bg-smasher-gold/20" : "bg-white/5"}`}><Ico className="w-6 h-6 text-smasher-gold" /></div>
                <div className={`font-heading font-black text-sm ${v.center ? "text-gradient-gold" : "text-white"}`}>{v.title}</div>
                {v.lines.map((l) => <div key={l} className="text-[11px] text-slate-400 leading-tight">{l}</div>)}
              </div>
            );
          })}
        </div>
      </section>

      {/* TODAY'S SPORTS */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 py-12">
        <SectionTitle kicker="LIVE & UPCOMING" title="Today's Sports" icon={Flame} />
        <UpcomingGames />
      </section>

      {/* FIGHT CARDS */}
      {fights.length > 0 && (
        <section className="max-w-[1500px] mx-auto px-4 sm:px-6 py-4">
          <SectionTitle kicker="BOXING & MMA" title="Upcoming Fight Cards" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{fights.slice(0, 3).map((e) => <FightCard key={e.id} event={e} />)}</div>
        </section>
      )}

      {/* FINAL SCORES */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 py-12">
        <SectionTitle kicker="RECENTLY COMPLETED" title="Final Scores" />
        {finals.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{finals.slice(0, 8).map((e) => <EventCard key={e.id} event={e} />)}</div>
        ) : (
          <div className="panel p-10 text-center text-slate-500">No completed games to show yet.</div>
        )}
      </section>

      {/* LATEST NEWS */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 py-12">
        <SectionTitle kicker="FROM THE SMASHER DESK" title="Latest Sports News" icon={Newspaper} action={<Link to="/news" className="text-sm font-bold text-smasher-gold hover:text-smasher-orange flex items-center gap-1">ALL NEWS <ChevronRight className="w-4 h-4" /></Link>} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{news.map((n) => <NewsCard key={n.id} item={n} />)}</div>
      </section>

      {/* EDUCATION */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 py-12">
        <SectionTitle kicker="LEARN THE GAME" title="Sportsbook Smasher Education" icon={BookOpen} action={<Link to="/education" className="text-sm font-bold text-smasher-gold hover:text-smasher-orange flex items-center gap-1">ALL TOPICS <ChevronRight className="w-4 h-4" /></Link>} />
        <p className="text-slate-400 max-w-2xl mb-6">Learn how to better understand sports wagers, odds, lines, and betting strategy. We teach you to analyze the game before you make the wager.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {EDUCATION_TOPICS.slice(0, 8).map((t) => (
            <Link key={t.slug} to={`/education/${t.slug}`} className="panel panel-hover p-5 group" data-testid={`edu-${t.slug}`}>
              <h3 className="font-heading font-bold text-lg text-white group-hover:text-smasher-gold transition">{t.title}</h3>
              <p className="text-sm text-slate-400 mt-1">{t.short}</p>
              <span className="text-xs text-smasher-gold font-bold mt-3 inline-flex items-center gap-1">LEARN <ChevronRight className="w-3.5 h-3.5" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* EBOOK PROMO */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 py-12">
        <div className="panel p-6 sm:p-10 grid md:grid-cols-[280px_1fr] gap-8 items-center">
          <img src={ASSETS.ebookCover} alt="Sportsbook Smasher: Football Edition" className="w-full max-w-[260px] mx-auto rounded-xl shadow-2xl" />
          <div>
            <div className="text-xs font-bold tracking-[0.2em] text-smasher-orange mb-2">SPORTSBOOK SMASHER</div>
            <h2 className="font-heading font-black uppercase text-3xl sm:text-4xl text-gradient-gold mb-3">Football Edition</h2>
            <p className="text-slate-300 leading-relaxed mb-5">The ebook that teaches sports betting concepts in plain English — from the three core bet types to bankroll management and a personal live-betting approach. Understand the wager before you place it.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/ebook" className="btn-gold px-6 py-3 rounded-xl font-heading font-black tracking-wide inline-flex items-center gap-2"><BookOpen className="w-5 h-5" /> GET THE EBOOK</Link>
              <Link to="/education/straight-bet" className="btn-ghost-gold px-6 py-3 rounded-xl font-heading font-black tracking-wide">LEARN THE BASICS</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 pb-16"><ResponsibleBanner /></section>
    </div>
  );
}
